import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader2, AlertCircle } from 'lucide-react';
import { containerSchema, type ContainerFormData } from '../schemas/import-tracking.schemas';
import { useImportTrackingStore } from '../store/import-tracking-store';
import type { Container } from '../types/import-tracking.types';

/**
 * Lo que la orden ya sabe y no hay que volver a pedir.
 *
 * El formulario pedía 6 campos y **4 los sabía el sistema**: el equipo se eligió al
 * cotizar, y los puertos y las fechas están en los tramos. Peor: los puertos eran
 * texto libre, así que se escribían a mano — y ahí aparecen cosas como "shaigan"
 * que después no matchean con nada.
 *
 * Quedan solo dos campos, y los dos **opcionales a propósito**:
 *
 *  · **N° de contenedor** — lo asigna la naviera cuando confirma el booking, que
 *    puede ser una o dos semanas después de crear la orden.
 *  · **N° de sello** — se pone cuando el contenedor se cierra físicamente en el
 *    puerto de origen, todavía más tarde.
 *
 * Exigirlos al crear obligaría a inventarlos, y un número de contenedor inventado
 * es peor que uno vacío: no se puede rastrear con la naviera.
 */
export interface ContainerDefaults {
  containerTypeId?: string | null;
  originPort?: string | null;
  destinationPort?: string | null;
  etd?: string | null;
  eta?: string | null;
  /** De dónde salieron, para poder decirlo en pantalla. */
  sourceLabel?: string;
}

interface ContainerFormDialogProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  container?: Container;
  onSuccess?: () => void;
  /** Datos que la orden ya conoce. Se precargan y no se piden. */
  defaults?: ContainerDefaults;
}

export function ContainerFormDialog({
  orderId,
  open,
  onOpenChange,
  container,
  onSuccess,
  defaults,
}: ContainerFormDialogProps) {
  const { containerTypes, createContainer, updateContainer } = useImportTrackingStore();
  const isEdit = !!container;
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * Se puede abrir para corregir lo precargado, pero cerrado por defecto.
   *
   * El caso normal es que lo precargado esté bien. Mostrar seis campos por si
   * acaso es hacerle leer seis para no cambiar ninguno.
   */
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContainerFormData>({
    resolver: zodResolver(containerSchema),
    defaultValues: {
      containerTypeId: '',
      containerNumber: '',
      sealNumber: '',
      originPort: '',
      destinationPort: '',
      etd: '',
      eta: '',
    },
  });

  const selectedTypeId = watch('containerTypeId');

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setShowAdvanced(false);

    if (container) {
      reset({
        containerTypeId: container.containerTypeId,
        containerNumber: container.containerNumber || '',
        sealNumber: container.sealNumber || '',
        originPort: container.originPort || '',
        destinationPort: container.destinationPort || '',
        etd: container.etd ? container.etd.slice(0, 10) : '',
        eta: container.eta ? container.eta.slice(0, 10) : '',
      });
      return;
    }

    // Contenedor nuevo: lo que la orden ya sabe entra precargado.
    reset({
      containerTypeId: defaults?.containerTypeId || '',
      containerNumber: '',
      sealNumber: '',
      originPort: defaults?.originPort || '',
      destinationPort: defaults?.destinationPort || '',
      etd: defaults?.etd?.slice(0, 10) || '',
      eta: defaults?.eta?.slice(0, 10) || '',
    });
  }, [open, container, defaults, reset]);

  const onSubmit = async (data: ContainerFormData) => {
    try {
      setSubmitError(null);
      if (isEdit && container) {
        await updateContainer(container.id, data);
      } else {
        await createContainer(orderId, data);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error || err?.message || 'Error al guardar el contenedor'
      );
    }
  };

  const containerTypeOptions = containerTypes.map((ct) => ({
    value: ct.id,
    label: `${ct.name} (${ct.code}) - ${ct.lengthCm}x${ct.widthCm}x${ct.heightCm} cm`,
  }));

  /** ¿La orden aportó algo? Si no, hay que pedir todo como antes. */
  const hasDefaults = Boolean(
    defaults?.containerTypeId || defaults?.originPort || defaults?.destinationPort
  );

  const selectedType = containerTypes.find((ct) => ct.id === selectedTypeId);
  const watchedOrigin = watch('originPort');
  const watchedDestination = watch('destinationPort');
  const watchedEtd = watch('etd');
  const watchedEta = watch('eta');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Contenedor' : 'Agregar Contenedor'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Modifique los datos del contenedor'
              : 'Configure un nuevo contenedor para esta orden'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/*
            Lo que la orden ya sabe se MUESTRA, no se pide.

            El equipo se eligió al cotizar y los puertos y fechas están en los
            tramos. Volver a pedirlos es pedir el mismo dato dos veces, y en texto
            libre además invita a escribirlos distinto.
          */}
          {!isEdit && hasDefaults && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {defaults?.sourceLabel ?? 'De la cotización'}
              </p>
              <dl className="space-y-1">
                {selectedType && (
                  <SummaryRow
                    label="Equipo"
                    value={`${selectedType.name} (${selectedType.code})`}
                  />
                )}
                {(watchedOrigin || watchedDestination) && (
                  <SummaryRow
                    label="Ruta"
                    value={`${watchedOrigin || '—'} → ${watchedDestination || '—'}`}
                  />
                )}
                {(watchedEtd || watchedEta) && (
                  <SummaryRow
                    label="Fechas"
                    value={`zarpa ${fmtDay(watchedEtd)} · llega ${fmtDay(watchedEta)}`}
                  />
                )}
              </dl>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-1.5 h-7 px-2 text-xs"
                onClick={() => setShowAdvanced((v) => !v)}
              >
                {showAdvanced ? 'Listo' : 'Corregir estos datos'}
              </Button>
            </div>
          )}

          {/*
            Los dos que SÍ van a mano — y los dos opcionales.

            El número lo asigna la naviera al confirmar el booking, días después. El
            sello se pone cuando el contenedor se cierra en el puerto de origen.
            Exigirlos ahora obligaría a inventarlos, y un número inventado es peor
            que uno vacío: no se puede rastrear.
          */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="containerNumber">N° de contenedor</Label>
              <Input
                id="containerNumber"
                {...register('containerNumber')}
                placeholder="MSKU1234567"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">Lo da la naviera al embarcar</p>
            </div>
            <div>
              <Label htmlFor="sealNumber">N° de sello</Label>
              <Input id="sealNumber" {...register('sealNumber')} placeholder="SEL001" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Se pone al cerrar en el puerto
              </p>
            </div>
          </div>

          {/* Lo precargado, editable solo si hace falta. */}
          {(isEdit || !hasDefaults || showAdvanced) && (
            <div className="space-y-4 rounded-lg border border-dashed p-3">
              <div>
                <Label>Tipo de contenedor *</Label>
                <SearchableSelect
                  options={containerTypeOptions}
                  value={selectedTypeId}
                  onValueChange={(val) => setValue('containerTypeId', val)}
                  placeholder="Seleccionar tipo..."
                  searchPlaceholder="Buscar tipo..."
                />
                {errors.containerTypeId && (
                  <p className="mt-1 text-xs text-destructive">{errors.containerTypeId.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="originPort">Puerto de origen</Label>
                  <Input id="originPort" {...register('originPort')} placeholder="Shanghái" />
                </div>
                <div>
                  <Label htmlFor="destinationPort">Puerto de destino</Label>
                  <Input
                    id="destinationPort"
                    {...register('destinationPort')}
                    placeholder="Arica"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="etd">Zarpa (estimado)</Label>
                  <Input id="etd" type="date" {...register('etd')} />
                </div>
                <div>
                  <Label htmlFor="eta">Llega (estimado)</Label>
                  <Input id="eta" type="date" {...register('eta')} />
                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{submitError}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Guardar Cambios' : 'Crear Contenedor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Una línea del resumen de lo que la orden ya sabe. */
function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs">
      <dt className="w-16 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** Fecha corta para el resumen. Guion si no hay. */
function fmtDay(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' });
}
